import { Component, ViewChildren, QueryList, ElementRef, ViewChild } from '@angular/core';
import { IonItemGroup, IonHeader, ModalController, AlertController, NavController } from '@ionic/angular';
import { MenuService } from 'src/app/services/food/menu.service';
import { SlugifyPipe } from 'src/app/pipes/slugify.pipe';
import { ActivatedRoute } from '@angular/router';
import { CartService } from 'src/app/services/cart/cart.service';
import { FoodCart, FoodCartItem } from '../../../../models/cart-models';
import { BottomModalComponent } from 'src/app/modals/bottom-modal/bottom-modal.component';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { I18ConfigService } from 'src/app/services/shared/I18-config.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics.service';
import { SeoService } from 'src/app/services/seo.service';
import { CustomizationComponent } from './customization/customization.component';
import { TranslateService } from '@ngx-translate/core';


interface FoodCategoryList {
  catName: string;
  sectionName: string;
}

@Component({
  selector: 'app-menu',
  templateUrl: './menu.page.html',
  styleUrls: ['./menu.page.scss'],
})
export class MenuPage {

  @ViewChildren(IonItemGroup, { read: ElementRef }) sectionList: QueryList<ElementRef>;
  @ViewChild(IonHeader, { read: ElementRef }) ionHeaderElement: ElementRef;

  currentSection = '';
  headerBorder: string;
  elevateHeader = 0;
  veg = '../../../../assets/icon/veg.svg';
  nonveg = '../../../../assets/icon/nonveg.svg';
  foodMenuList: any[];
  foodCategoryList: FoodCategoryList[];
  cart: FoodCart;
  locationId: number;
  storeId: number;
  currencyCode: string;
  totalQuantity: number;
  totalPrice: number;
  timer: any;
  noData: number;
  storeName: string;
  replaceTitle: string;
  replaceText: string;
  noBtn: string;
  yesBtn: string;
  // tslint:disable-next-line: max-line-length
  constructor(private seoService: SeoService, private menuService: MenuService, private slugifyPipe: SlugifyPipe, private activatedRoute: ActivatedRoute, private modalController: ModalController, private cartService: CartService, private alertController: AlertController, private loaderService: LoaderService, private navControl: NavController, private i18ConfigService: I18ConfigService, private googleAnalyticsService: GoogleAnalyticsService, private translate: TranslateService) {
    this.initState();
    this.currencyCode = i18ConfigService.getCurrency();
  }

  ionViewDidEnter() {
    // get menu list based on parameters in url
    // tslint:disable-next-line: no-string-literal
    this.locationId = this.activatedRoute.snapshot.params['loc'];
    // tslint:disable-next-line: no-string-literal
    this.storeName = this.activatedRoute.snapshot.params['storeName'];
    this.seoService.updatePageTitle(this.storeName + '|menu');

    // tslint:disable-next-line: no-string-literal
    this.storeId = this.activatedRoute.snapshot.params['storeId'];
    if (this.locationId && this.storeId) {
      this.getMenuData();
    } else {
      // show error page
    }
    this.cartService.foodCartSubject.subscribe((newCartData) => {
      // console.log('cartData', newCartData);
      this.totalQuantity = 0;
      this.totalPrice = 0;
      this.cart = newCartData;
      this.cart.list.forEach((element) => {
        this.totalQuantity += element.quantity;
        this.totalPrice += (element.price || 0) * element.quantity;
      });
    });
  }


  private initState() {
    this.noData = 0;
    this.foodCategoryList = null;
    this.foodMenuList = null;
    this.currentSection = null;
    this.headerBorder = '';
    this.cart = {} as FoodCart;
    this.totalQuantity = 0;
    this.totalPrice = 0;
  }


  private getMenuData() {
    this.initState();
    this.menuService.getMenuList(this.locationId, this.storeId).subscribe(response => {
      if (response.storeName) {
        this.storeName = response.storeName;
      }
      if (response.cartlist && response.cartlist.length > 0) {
        this.cart = this.cartService.initializeCartData(response.cartlist, this.storeId, this.locationId);
        this.cart.list.forEach((element) => {
          this.totalQuantity += element.quantity;
          this.totalPrice += (element.price || 0) * element.quantity;
        });
      }
      this.updateFoodMenuPageState(response.food_items);
    });
  }


  public updateFoodMenuPageState(foodMenuItems = []) {
    if (!foodMenuItems || foodMenuItems.length === 0) {
      this.noData = 1;
      return;
    } else {
      this.foodMenuList = foodMenuItems;
      this.foodCategoryList = [];
      this.foodMenuList.forEach((item, index) => {
        if (index === 0) {
          this.currentSection = this.slugifyPipe.transform(item.categoryName);
        }
        this.foodCategoryList.push({ catName: item.categoryName, sectionName: this.slugifyPipe.transform(item.categoryName) });
      });
    }
  }


  public async addItemToCart(menuItem, optionList?, additionalPrice = 0) {
    // check if cart has items from different stores
    let clearCart = 0;
    if (typeof this.cartService.foodCart.locationId !== 'undefined' && this.cartService.foodCart.locationId !== this.locationId) {
      if (! await this.presentClearCartAlert()) {
        return;
      } else {
        clearCart = 1;
      }
    }
    await this.loaderService.presentLoading('Updating Cart');
    let cartItem: FoodCartItem = menuItem as FoodCartItem;
    cartItem.locationId = this.locationId;
    cartItem.optionList = optionList || [];
    cartItem.quantity = 1;
    cartItem.additionalPrice = additionalPrice;
    let status = await this.cartService.addMenuItem(cartItem, menuItem.storeId, this.locationId, clearCart);
    if (status) {
      menuItem.itemQuantityInCart = this.cartService.getQuantityByMenuId(menuItem.id);
    } else {
      // Api failed to update cart state
    }
    if (menuItem.itemQuantityInCart > 0) {
      menuItem.itemInCart = 1;
    } else {
      menuItem.itemInCart = 0;
    }
    await this.loaderService.loading.dismiss();

    this.googleAnalyticsService.trackEvent('addtocart', 'click', 'food', menuItem.id);
  }


  public async removeItemFromCart(menuItem, optionList?) {
    // tslint:disable-next-line: prefer-const
    let cartItem: FoodCartItem = menuItem as FoodCartItem;
    cartItem.locationId = this.locationId;
    cartItem.optionList = optionList || [];
    cartItem.quantity = 1;
    await this.loaderService.presentLoading('Updating Cart');
    // tslint:disable-next-line: prefer-const
    let status = await this.cartService.removeCartItemById(cartItem);
    console.log('status', status);
    if (status) {
      menuItem.itemQuantityInCart = this.cartService.getQuantityByMenuId(cartItem.id);
    } else {
      // Api failed
    }
    if (menuItem.itemQuantityInCart > 0) {
      menuItem.itemInCart = 1;
    } else {
      menuItem.itemInCart = 0;
    }
    await this.loaderService.loading.dismiss();
  }




  async customizeItem(menuItem) {
    console.log('menuItem', menuItem);
    const modal = await this.modalController.create({
      component: CustomizationComponent,
      componentProps: {
        menuItem,
      },
      backdropDismiss: true
    });
    modal.onDidDismiss().then((response) => {
      if (response.data && response.data.selectedMenuOptions && response.data.selectedMenuOptions.length > 0) {
        this.addItemToCart(menuItem, response.data.selectedMenuOptions, response.data.additionalPrice || 0);
      }
    });
    return await modal.present();
  }


  async presentGoToCartAlert() {
    const alert = await this.alertController.create({
      header: 'Remove Item From Cart',
      message: 'This Item has multiple customizations added. Proceed to cart to remove item?',
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => {
          }
        }, {
          text: 'Yes',
          handler: () => {
            this.navControl.navigateForward(['shopping/food/cart']);
            // this.ngZone.run(() => this.router.navigate(['shopping/food/cart']));
          }
        }
      ]
    });
    await alert.present();
  }

  async presentClearCartAlert(): Promise<boolean> {
    this.translate.get('MENU.replace_msg_title', {value: 'world'}).subscribe((res: string) => {
      this.replaceTitle = res;
    });
    this.translate.get('MENU.replace_msg_text', {value: 'world'}).subscribe((res: string) => {
      this.replaceText = res;
    });
    this.translate.get('MENU.no', {value: 'world'}).subscribe((res: string) => {
      this.noBtn = res;
    });
    this.translate.get('MENU.yes', {value: 'world'}).subscribe((res: string) => {
      this.yesBtn = res;
    });
    const alert = await this.alertController.create({
      header: this.replaceTitle,
      message: this.replaceText,
      buttons: [
        {
          text: this.noBtn,
          role: 'cancel',
          cssClass: 'secondary'
        }, {
          text: this.yesBtn,
          role: 'continue'
        }
      ]
    });
    await alert.present();
    let response = await alert.onDidDismiss();
    if (response.role === 'continue') {
      return true;
    } else {
      return false;
    }
  }


  async presentRepeatItemModal(menuItem) {
    const modal = await this.modalController.create({
      component: BottomModalComponent,
      cssClass: 'tx-bottom-modal',
      componentProps: {
        menuItem,
        currencyCode: this.currencyCode
      },
      backdropDismiss: true
    });
    modal.onDidDismiss().then((response) => {
      if (response.data && response.data.methodType) {
        if (response.data.methodType === 'customize') {
          this.customizeItem(menuItem);
        } else {
          let dataFromCart: FoodCartItem;
          // tslint:disable-next-line: prefer-const
          let foodCart = this.cart;
          foodCart.list.reverse().forEach((currentItem, index) => {
            if (typeof dataFromCart === 'undefined' && menuItem.id === currentItem.id) {
              dataFromCart = currentItem;
            }
          });
          if (dataFromCart && dataFromCart.id) {
            this.addItemToCart(menuItem, dataFromCart.optionList, dataFromCart.price - menuItem.price || 0);
          }
        }
      }

    });
    return await modal.present();
  }


  //  UI Interations and animations
  scrollTo(section) {
    this.focusSection(section);
  }

  private focusSection(segmentId) {
    document.getElementById(segmentId).scrollIntoView({ behavior: 'smooth' });
  }

  private focusSegment(segmentId) {
    document.getElementById(segmentId).scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    });
  }

  public onContentScroll(event) {
    const scrollTop = event.detail.scrollTop;
    if (scrollTop > 0 && this.elevateHeader === 0) {
      this.elevateHeader = 1;
    } else if (scrollTop <= 0 && this.elevateHeader === 1) {
      this.elevateHeader = 0;
    }
    let selectedItem;
    const parentOffset = event.target.offsetTop;
    this.sectionList.forEach(element => {
      if ((element.nativeElement.offsetTop - parentOffset) <= scrollTop) { // checking if section is in viewport at top
        selectedItem = element.nativeElement.id;
      }
    });
    clearTimeout(this.timer);
    if (selectedItem) {
      this.timer = setTimeout(() => {
        this.currentSection = selectedItem;
        this.focusSegment('tab-' + this.currentSection);
      }, 250);
    }
  }


}
